import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

/**
 * Catches a render crash and shows it, instead of React unmounting the whole
 * tree and leaving a white page.
 *
 * Worth having specifically because two things here can throw on data the user
 * typed: a chart handed a shape it didn't expect, and a strategy JSON pasted
 * into the Workbench. Neither should cost you the rest of the app, and
 * "reload and lose your draft" is not a recovery.
 *
 * Keyed by route in App.tsx, so navigating away clears a crashed page rather
 * than leaving the error stuck until a manual reload.
 */
interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Render error:", error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card">
        <div className="card__body">
          <div className="empty-state">
            <div className="empty-state__icon">⚠</div>
            <div className="empty-state__title">This page hit an error</div>
            <div className="empty-state__body">
              The rest of the app is still fine — switch pages using the bar above, or try again. Nothing you
              saved has been lost.
            </div>
            <pre className="docs__code" style={{ marginTop: "1rem", textAlign: "left" }}>
              {error.message}
            </pre>
            <div style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => this.setState({ error: null })}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
